import { computed, ref, Ref } from 'vue';
import { IActUserToAdd, ActAddedUserTypes } from '@/models/act/act-access.interface';
import ValidationHandler from '@/presenters/validation-handler';
import actStore from '@/store/act';

export default class ActAddUsersCode {
	public addUsersInputField: Ref<HTMLInputElement | null> = ref(null);
	private emailSeparator = ',';

	public invalidEntriesMessage = computed((): string => {
		const parts: string[] = [];
		const notEmailCount = this.amountOfNonEmailEntries.value;
		const alreadyAddedCount = this.amountOfDuplicateEntries.value;

		if (notEmailCount > 0) {
			parts.push(`${notEmailCount} invalid`);
		}

		if (alreadyAddedCount > 0) {
			parts.push(`${alreadyAddedCount} duplicate`);
		}

		if (parts.length === 0) {
			return '';
		}

		const emailWord = (notEmailCount + alreadyAddedCount) === 1 ? 'email' : 'emails';
		return `${parts.join(' and ')} ${emailWord} couldn't be added. Please review before continuing!`;
	});

	public amountOfUsersBasedSentence = computed((): string => {
		const count = this.amountOfValidEntries.value;
		return `${count} email${count !== 1 ? 's' : ''} added`;
	});

	public actUsersToAdd = computed((): IActUserToAdd[] => {
		return actStore.actGetters.actUsersToAdd();
	});

	private amountOfValidEntries = computed((): number => {
		return this.actUsersToAdd.value
			.filter(entry => entry.type === ActAddedUserTypes.VALID).length;
	});

	private amountOfNonEmailEntries = computed((): number => {
		return this.actUsersToAdd.value
			.filter(entry => entry.type === ActAddedUserTypes.NOT_EMAIL).length;
	});

	private amountOfDuplicateEntries = computed((): number => {
		return this.actUsersToAdd.value
			.filter(entry => entry.type === ActAddedUserTypes.ALREADY_ADDED).length;
	});

	public handleTextInput(event: KeyboardEvent): void {
		if (event.key !== ',') {
			return;
		}

		event.preventDefault();
		this.analyseTextInputForUsersToAdd();
	}

	public async handlePastInput(): Promise<void> {
		// Wait for the pasted content to be in the input field.
		await new Promise(resolve => requestAnimationFrame(resolve));
		this.analyseTextInputForUsersToAdd();
	}

	public isEntryInvalid(entry: IActUserToAdd): boolean {
		return entry.type !== ActAddedUserTypes.VALID;
	}

	public removeUserToAdd(index: number): void {
		actStore.actMutations.removeActUserToAdd(index);
	}

	public analyseTextInputForUsersToAdd(): void {
		if (!this.addUsersInputField.value) {
			return;
		}

		this.addUsersInputField.value.value
			.split(this.emailSeparator)
			.map(email => email.toLowerCase().trim())
			.forEach(this.storeUserToAdd.bind(this));

		this.addUsersInputField.value.value = '';
	}

	private storeUserToAdd(email: string): void {
		if (!email.length) {
			return;
		}

		if (!ValidationHandler.isEmail(email)) {
			actStore.actMutations.addActUserToAdd({
				value: email,
				type: ActAddedUserTypes.NOT_EMAIL
			});
			return;
		}

		const userAlreadyAdded = this.actUsersToAdd.value
			.some(user => user.value === email);
		if (userAlreadyAdded) {
			actStore.actMutations.addActUserToAdd({
				value: email,
				type: ActAddedUserTypes.ALREADY_ADDED
			});
			return;
		}

		actStore.actMutations.addActUserToAdd({
			value: email,
			type: ActAddedUserTypes.VALID
		});
	}
}
